import { ref, computed, nextTick, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

// Import API functions for entity detection
import {
  callFetchUser,
  callFetchOrganization,
  callFetchInvite
} from "@/api/identity";
import {
  callFetchEntitlement,
  callFetchProduct,
  callFetchTemplateById
} from "@/api/licensing";

interface SearchItem {
  id: string;
  title: string;
  subtitle?: string;
  type: string;
  icon: string;
  route?: string;
  action?: string;
  description?: string;
}

interface SearchSection {
  title: string;
  items: SearchItem[];
}

interface GuessedEntity {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  route: string;
  icon: string;
  data?: any;
}

// Global shared state - will be created once and reused
const globalState = {
  isOpen: ref(false),
  searchQuery: ref(""),
  selectedIndex: ref(0),
  maxResults: ref(8),
  // Add action event emitter
  actionEmitter: ref<((action: string, payload?: any) => void) | null>(null),
  // Add guess entity state
  isGuessing: ref(false),
  guessedEntity: ref<GuessedEntity | null>(null),
  guessError: ref<string | null>(null)
};

export function useGlobalSearch() {
  const router = useRouter();
  const { t } = useI18n();

  // Use the global shared state
  const isOpen = globalState.isOpen;
  const searchQuery = globalState.searchQuery;
  const selectedIndex = globalState.selectedIndex;
  const maxResults = globalState.maxResults;
  const actionEmitter = globalState.actionEmitter;
  const isGuessing = globalState.isGuessing;
  const guessedEntity = globalState.guessedEntity;
  const guessError = globalState.guessError;

  // Entity types configuration
  const entityTypes = [
    {
      type: "users",
      icon: "material-icons-fingerprint",
      basePath: "/admin/identity/users",
      detailRoute: "user",
      searchPlaceholder: "Search users...",
      color: "#3498db"
    },
    {
      type: "organizations",
      icon: "material-icons-corporate_fare",
      basePath: "/admin/identity/organizations",
      detailRoute: "organization",
      searchPlaceholder: "Search organizations...",
      color: "#e74c3c"
    },
    {
      type: "invites",
      icon: "material-icons-card_membership",
      basePath: "/admin/identity/invites",
      detailRoute: "invite",
      searchPlaceholder: "Search invites...",
      color: "#f39c12"
    },
    {
      type: "entitlements",
      icon: "material-icons-card_membership",
      basePath: "/admin/licensing/entitlements",
      detailRoute: "entitlement",
      searchPlaceholder: "Search entitlements...",
      color: "#9b59b6"
    },
    {
      type: "templates",
      icon: "material-icons-card_membership",
      basePath: "/admin/products/templates",
      detailRoute: "template",
      searchPlaceholder: "Search templates...",
      color: "#1abc9c"
    },
    {
      type: "products",
      icon: "material-icons-card_membership",
      basePath: "/admin/products",
      detailRoute: "product",
      searchPlaceholder: "Search products...",
      color: "#34495e"
    }
  ];

  // Quick actions using modals
  const quickActions = computed(() => {
    try {
      return [
        {
          id: "action-create-organization",
          title: t("menu.createOrganization") || "Create Organization",
          subtitle:
            t("organizations.createModal.title") || "Create a new organization",
          type: "action",
          icon: "material-icons-add_business",
          action: "create-organization",
          description: "Open modal to create a new organization"
        },
        {
          id: "action-create-entitlement",
          title: t("menu.createEntitlement") || "Create Entitlement",
          subtitle:
            t("entitlements.createModal.title") || "Create a new entitlement",
          type: "action",
          icon: "material-icons-add_circle",
          action: "create-entitlement",
          description: "Open modal to create a new entitlement"
        },
        {
          id: "action-breadcrumb-settings",
          title: t("menu.breadcrumbSettings") || "Breadcrumb Settings",
          subtitle:
            t("breadcrumb.settingsModal.title") ||
            "Configure breadcrumb settings",
          type: "action",
          icon: "material-icons-settings",
          action: "breadcrumb-settings",
          description: "Open modal to configure breadcrumb settings"
        }
      ];
    } catch (error) {
      // Fallback when i18n is not available
      console.warn("i18n not available, using fallback action items");
      return [
        {
          id: "action-create-organization",
          title: "Create Organization",
          subtitle: "Create a new organization",
          type: "action",
          icon: "material-icons-add_business",
          action: "create-organization",
          description: "Open modal to create a new organization"
        },
        {
          id: "action-create-entitlement",
          title: "Create Entitlement",
          subtitle: "Create a new entitlement",
          type: "action",
          icon: "material-icons-add_circle",
          action: "create-entitlement",
          description: "Open modal to create a new entitlement"
        },
        {
          id: "action-breadcrumb-settings",
          title: "Breadcrumb Settings",
          subtitle: "Configure breadcrumb settings",
          type: "action",
          icon: "material-icons-settings",
          action: "breadcrumb-settings",
          description: "Open modal to configure breadcrumb settings"
        }
      ];
    }
  });

  // Navigation items (pages)
  const navigationItems = computed(() => {
    try {
      return [
        {
          id: "nav-users",
          title: t("menu.users") || "Users",
          subtitle: t("breadcrumb.users") || "Users",
          type: "navigation",
          icon: "material-icons-fingerprint",
          route: "/admin/identity/users",
          description: "Manage user accounts and profiles"
        },
        {
          id: "nav-organizations",
          title: t("menu.organizations") || "Organizations",
          subtitle: t("breadcrumb.organizations") || "Organizations",
          type: "navigation",
          icon: "material-icons-corporate_fare",
          route: "/admin/identity/organizations",
          description: "Manage organizations and their members"
        },
        {
          id: "nav-invites",
          title: t("menu.invites") || "Invites",
          subtitle: t("breadcrumb.invites") || "Invites",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/identity/invites",
          description: "Manage pending invitations"
        },
        {
          id: "nav-entitlements",
          title: t("menu.entitlements") || "Entitlements",
          subtitle: t("breadcrumb.entitlements") || "Entitlements",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/licensing/entitlements",
          description: "Manage licenses and entitlements"
        },
        {
          id: "nav-templates",
          title: t("menu.templates") || "Templates",
          subtitle: t("breadcrumb.templates") || "Templates",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/products/templates",
          description: "Manage license templates"
        },
        {
          id: "nav-products",
          title: t("menu.products") || "Products",
          subtitle: t("breadcrumb.products") || "Products",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/products",
          description: "Manage product catalog"
        }
      ];
    } catch (error) {
      // Fallback when i18n is not available
      console.warn("i18n not available, using fallback navigation items");
      return [
        {
          id: "nav-users",
          title: "Users",
          subtitle: "Users",
          type: "navigation",
          icon: "material-icons-fingerprint",
          route: "/admin/identity/users",
          description: "Manage user accounts and profiles"
        },
        {
          id: "nav-organizations",
          title: "Organizations",
          subtitle: "Organizations",
          type: "navigation",
          icon: "material-icons-corporate_fare",
          route: "/admin/identity/organizations",
          description: "Manage organizations and their members"
        },
        {
          id: "nav-invites",
          title: "Invites",
          subtitle: "Invites",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/identity/invites",
          description: "Manage pending invitations"
        },
        {
          id: "nav-entitlements",
          title: "Entitlements",
          subtitle: "Entitlements",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/licensing/entitlements",
          description: "Manage licenses and entitlements"
        },
        {
          id: "nav-templates",
          title: "Templates",
          subtitle: "Templates",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/products/templates",
          description: "Manage license templates"
        },
        {
          id: "nav-products",
          title: "Products",
          subtitle: "Products",
          type: "navigation",
          icon: "material-icons-card_membership",
          route: "/admin/products",
          description: "Manage product catalog"
        }
      ];
    }
  });

  // Search suggestions for entity IDs
  const entitySuggestions = computed(() => {
    if (!searchQuery.value.trim()) {
      return [];
    }

    const query = searchQuery.value.toLowerCase().trim();
    const suggestions: SearchItem[] = [];

    // Check if query looks like an ID (UUID pattern or simple ID)
    const isLikelyId = /^[a-f0-9-]{8,}|^\d+$/.test(query);

    if (isLikelyId || query.length >= 3) {
      entityTypes.forEach(entityType => {
        suggestions.push({
          id: `${entityType.type}-${query}`,
          title: `Go to ${entityType.type.slice(0, -1)} "${query}"`,
          subtitle: `Navigate to ${entityType.type} detail page`,
          type: "entity",
          icon: entityType.icon,
          route: `${entityType.basePath}/${query}`,
          description: `Direct navigation to ${entityType.type} with ID: ${query}`
        });
      });
    }

    return suggestions.slice(0, maxResults.value);
  });

  // Function to guess entity type by trying different APIs
  const guessEntity = async (id: string) => {
    if (!id.trim()) {
      return;
    }

    isGuessing.value = true;
    guessedEntity.value = null;
    guessError.value = null;

    const entityCheckers = [
      {
        type: "user",
        icon: "material-icons-fingerprint",
        apiCall: () => callFetchUser(id),
        getTitle: (data: any) => data.name || data.email || `User ${id}`,
        getSubtitle: (data: any) =>
          data.email || data.principalName || "User account",
        getRoute: (id: string) => `/admin/identity/users/${id}`
      },
      {
        type: "organization",
        icon: "material-icons-corporate_fare",
        apiCall: () => callFetchOrganization(id),
        getTitle: (data: any) => data.name || `Organization ${id}`,
        getSubtitle: (data: any) => data.description || "Organization",
        getRoute: (id: string) => `/admin/identity/organizations/${id}`
      },
      {
        type: "invite",
        icon: "material-icons-card_membership",
        apiCall: () => callFetchInvite(id),
        getTitle: (data: any) => data.email || `Invite ${id}`,
        getSubtitle: (data: any) =>
          `Invite for ${data.organizationName || "organization"}`,
        getRoute: (id: string) => `/admin/identity/invites/${id}`
      },
      {
        type: "entitlement",
        icon: "material-icons-card_membership",
        apiCall: () => callFetchEntitlement(id),
        getTitle: (data: any) => data.name || `Entitlement ${id}`,
        getSubtitle: (data: any) => `Product: ${data.productName || "Unknown"}`,
        getRoute: (id: string) => `/admin/licensing/entitlements/${id}`
      },
      {
        type: "product",
        icon: "material-icons-card_membership",
        apiCall: () => callFetchProduct(id),
        getTitle: (data: any) => data.name || `Product ${id}`,
        getSubtitle: (data: any) => data.description || "Product",
        getRoute: (id: string) => `/admin/products/${id}`
      },
      {
        type: "template",
        icon: "material-icons-card_membership",
        apiCall: () => callFetchTemplateById(id),
        getTitle: (data: any) => data.name || `Template ${id}`,
        getSubtitle: (data: any) => data.description || "License template",
        getRoute: (id: string) => `/admin/products/templates/${id}`
      }
    ];

    for (const checker of entityCheckers) {
      try {
        const response = await checker.apiCall();
        if (response && (response as any).data) {
          const entityData = (response as any).data;
          guessedEntity.value = {
            type: checker.type,
            id,
            title: checker.getTitle(entityData),
            subtitle: checker.getSubtitle(entityData),
            route: checker.getRoute(id),
            icon: checker.icon,
            data: entityData
          };
          isGuessing.value = false;
          return;
        }
      } catch (error) {
        // Continue to next checker if this one fails
        continue;
      }
    }

    // If we reach here, no entity was found
    guessError.value = `No entity found with ID: ${id}`;
    isGuessing.value = false;
  };

  // Reset guess state when search query changes
  watch(searchQuery, (newQuery, oldQuery) => {
    // Reset guess state when query changes or is cleared
    if (!newQuery.trim() || newQuery.trim() !== oldQuery?.trim()) {
      guessedEntity.value = null;
      guessError.value = null;
      isGuessing.value = false;
    }
  });

  // Filter navigation items based on search
  const filteredNavigation = computed(() => {
    if (!searchQuery.value.trim()) {
      return navigationItems.value;
    }

    const query = searchQuery.value.toLowerCase();
    return navigationItems.value.filter(
      item =>
        item.title.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.subtitle.toLowerCase().includes(query)
    );
  });

  // Filter actions based on search
  const filteredActions = computed(() => {
    if (!searchQuery.value.trim()) {
      return quickActions.value;
    }

    const query = searchQuery.value.toLowerCase();
    return quickActions.value.filter(
      item =>
        item.title.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.subtitle.toLowerCase().includes(query)
    );
  });

  // All search results grouped by sections
  const searchResults = computed((): SearchSection[] => {
    const sections: SearchSection[] = [];

    if (!searchQuery.value.trim()) {
      // Show all navigation when no search
      sections.push({
        title: "Navigation",
        items: navigationItems.value.slice(0, maxResults.value)
      });

      // Show actions when no search
      if (filteredActions.value.length > 0) {
        sections.push({
          title: "Quick Actions",
          items: filteredActions.value.slice(0, maxResults.value)
        });
      }
    } else {
      // Show filtered navigation
      if (filteredNavigation.value.length > 0) {
        sections.push({
          title: "Pages",
          items: filteredNavigation.value.slice(0, maxResults.value)
        });
      }

      // Show filtered actions
      if (filteredActions.value.length > 0) {
        sections.push({
          title: "Actions",
          items: filteredActions.value.slice(0, maxResults.value)
        });
      }

      // Show guess entity section
      const query = searchQuery.value.trim();

      if (query.length >= 3) {
        const guessItems: SearchItem[] = [];

        if (isGuessing.value) {
          guessItems.push({
            id: `guess-loading-${query}`,
            title: "Finding entity...",
            subtitle: `Looking up ID: ${query}`,
            type: "guess-loading",
            icon: "material-icons-autorenew",
            description: "Checking all entity types"
          });
        } else if (guessedEntity.value) {
          guessItems.push({
            id: `guess-found-${query}`,
            title: guessedEntity.value.title,
            subtitle: `Found ${guessedEntity.value.type}: ${guessedEntity.value.subtitle}`,
            type: "guess-found",
            icon: guessedEntity.value.icon,
            route: guessedEntity.value.route,
            description: `Navigate to ${guessedEntity.value.type}`
          });
        } else if (guessError.value) {
          guessItems.push({
            id: `guess-error-${query}`,
            title: "Entity not found",
            subtitle: guessError.value,
            type: "guess-error",
            icon: "material-icons-error",
            description: "No entity found with this ID"
          });
        } else {
          guessItems.push({
            id: `guess-action-${query}`,
            title: "Find Entity",
            subtitle: `Try to identify what "${query}" is`,
            type: "guess-action",
            icon: "material-icons-search",
            action: "guess-entity",
            description: "Automatically detect entity type"
          });
        }

        if (guessItems.length > 0) {
          sections.push({
            title: "Find Entity",
            items: guessItems
          });
        }
      }

      // Show entity suggestions
      if (entitySuggestions.value.length > 0) {
        sections.push({
          title: "Go to Entity",
          items: entitySuggestions.value
        });
      }
    }

    return sections;
  });

  // Flatten all items for keyboard navigation
  const allItems = computed(() => {
    return searchResults.value.flatMap(section => section.items);
  });

  // Open search modal
  const open = () => {
    console.log("Opening global search modal"); // Debug log
    isOpen.value = true;
    selectedIndex.value = 0;
    searchQuery.value = "";

    // Debug logs to see what's happening
    console.log("Navigation items:", navigationItems.value);
    console.log("Search results:", searchResults.value);
    console.log("All items:", allItems.value);
  };

  // Close search modal
  const close = () => {
    console.log("Closing global search modal"); // Debug log
    isOpen.value = false;
    searchQuery.value = "";
    selectedIndex.value = 0;
  };

  // Navigate to selected item or execute action
  const selectItem = (item?: SearchItem) => {
    const itemToSelect = item || allItems.value[selectedIndex.value];
    if (itemToSelect) {
      // Handle guess entity action without closing modal
      if (itemToSelect.action === "guess-entity") {
        console.log("Executing guess entity action"); // Debug log
        const query = searchQuery.value.trim();
        guessEntity(query);
        return; // Don't close modal
      }

      close();

      // Handle other actions
      if (itemToSelect.action && actionEmitter.value) {
        console.log("Executing action:", itemToSelect.action); // Debug log
        actionEmitter.value(itemToSelect.action);
      }
      // Handle navigation
      else if (itemToSelect.route) {
        console.log("Navigating to:", itemToSelect.route); // Debug log
        router.push(itemToSelect.route);
      }
    }
  };

  // Keyboard navigation
  const moveUp = () => {
    if (allItems.value.length === 0) {
      return;
    }

    if (selectedIndex.value > 0) {
      selectedIndex.value--;
    } else {
      selectedIndex.value = allItems.value.length - 1;
    }
  };

  const moveDown = () => {
    if (allItems.value.length === 0) {
      return;
    }

    if (selectedIndex.value < allItems.value.length - 1) {
      selectedIndex.value++;
    } else {
      selectedIndex.value = 0;
    }
  };

  // Handle keyboard shortcuts
  const handleKeydown = (event: KeyboardEvent) => {
    if (!isOpen.value) {
      return;
    }

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        moveUp();
        break;
      case "ArrowDown":
        event.preventDefault();
        moveDown();
        break;
      case "Enter":
        event.preventDefault();
        selectItem();
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
    }
  };

  // Watch for search query changes to reset selected index
  watch(searchQuery, () => {
    selectedIndex.value = 0;
  });

  // Setup global keyboard shortcuts
  const setupKeyboardShortcuts = () => {
    const handleGlobalKeydown = (event: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        console.log("Keyboard shortcut pressed", { isOpen: isOpen.value }); // Debug log
        if (isOpen.value) {
          close();
        } else {
          open();
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeydown);
    console.log("Global search keyboard shortcuts initialized"); // Debug log

    // Return cleanup function
    return () => {
      document.removeEventListener("keydown", handleGlobalKeydown);
      console.log("Global search keyboard shortcuts cleaned up"); // Debug log
    };
  };

  // Register action emitter
  const registerActionEmitter = (
    emitter: (action: string, payload?: any) => void
  ) => {
    actionEmitter.value = emitter;
  };

  return {
    // State
    isOpen,
    searchQuery,
    selectedIndex,
    isGuessing,
    guessedEntity,
    guessError,

    // Computed
    searchResults,
    allItems,

    // Methods
    open,
    close,
    selectItem,
    moveUp,
    moveDown,
    handleKeydown,
    setupKeyboardShortcuts,
    registerActionEmitter,
    guessEntity
  };
}
